import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import useMeta from "@/hooks/useMeta";
import useFavorites from "@/hooks/useFavorites";
import { works } from "@/mocks/works";
import { TYPE_LABELS, TYPE_PATHS } from "@/types/media";
import WorkCard from "@/components/base/WorkCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isFavorite, toggle } = useFavorites();

  const work = useMemo(() => works.find((w) => w.id === id), [id]);

  useMeta(
    work
      ? {
          title: `${work.title}详情介绍 - 大象传媒`,
          description: `《${work.title}》是${work.year}年${work.region}${TYPE_LABELS[work.type]}，由${work.director}执导，${work.cast.slice(0, 3).join("、")}等主演。${work.synopsis.slice(0, 60)}`,
          keywords: `${work.title},${work.title}简介,${work.title}演员,${TYPE_LABELS[work.type]}推荐,大象传媒`,
        }
      : {
          title: "作品不存在 - 大象传媒",
          description: "该影视作品不存在或已下架，请返回大象传媒浏览更多影视内容。",
          keywords: "大象传媒,影视作品",
        }
  );

  if (!work) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该影视作品不存在或已下架"
          description="您访问的影视作品可能已被移除，不妨看看下方的其他精彩内容。"
        />
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="rounded-md border border-primary-300 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
          >
            浏览影视
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(work.id);

  const related = works
    .filter((w) => w.id !== work.id)
    .map((w) => {
      const sameType = w.type === work.type ? 2 : 0;
      const sharedGenres = w.genres.filter((g) => work.genres.includes(g)).length;
      return { work: w, score: sameType + sharedGenres };
    })
    .sort((a, b) => b.score - a.score || b.work.rating - a.work.rating)
    .slice(0, 5)
    .map((item) => item.work);

  const metaRows = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "类型", value: work.genres.join(" / ") },
    { label: "年份", value: String(work.year) },
    { label: "地区", value: work.region },
    { label: "时长", value: work.duration },
  ];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "影视分类" },
          { label: TYPE_LABELS[work.type], to: TYPE_PATHS[work.type] },
          { label: work.title },
        ]}
      />

      <div className="relative mt-4 overflow-hidden rounded-lg bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src={work.backdrop}
            alt={work.title}
            title={`${work.title} 主图`}
            className="h-full w-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/40"></div>
        </div>

        <div className="relative flex flex-col gap-6 p-5 md:flex-row md:p-8">
          <div className="h-72 w-48 shrink-0 overflow-hidden rounded-lg border border-background-50/10 bg-background-900 md:h-80 md:w-56">
            <img
              src={work.poster}
              alt={work.title}
              title={`${work.title} 封面`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2.5 py-0.5 text-xs font-semibold text-background-50">
                {TYPE_LABELS[work.type]}
              </span>
              {work.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-50/15 px-2.5 py-0.5 text-xs text-background-100"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {work.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-background-100/90">
              <span className="flex items-center gap-1 text-accent-400">
                <i className="ri-star-fill text-lg"></i>
                <strong className="text-xl font-bold">{work.rating.toFixed(1)}</strong>
              </span>
              <span>{work.year}</span>
              <span>{work.region}</span>
              <span>{work.duration}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line"></i>
                热度 {work.popularity}
              </span>
            </div>

            <div className="mt-5">
              <button
                type="button"
                onClick={() => toggle(work.id)}
                className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-background-50 text-foreground-950 hover:bg-background-100"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"}></i>
                {favorited ? "已收藏" : "收藏作品"}
              </button>
              <p className="mt-2 text-xs text-background-100/60">
                收藏功能为浏览器本地演示，仅保存在当前设备
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600 text-background-50">
                <i className="ri-file-text-line text-sm"></i>
              </span>
              剧情简介
            </h2>
            <p className="text-sm leading-7 text-foreground-700 md:text-base">
              {work.synopsis}
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              《{work.title}》是一部{work.year}年出品的{work.region}
              {TYPE_LABELS[work.type]}，题材涵盖{work.genres.join("、")}。影片以细腻的叙事与鲜明的影像风格，呈现出独特的艺术表达，深受观众喜爱。本页内容为本站整理的作品资料，供影视爱好者参考。
            </p>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600 text-background-50">
                <i className="ri-list-check-2 text-sm"></i>
              </span>
              基础信息
            </h2>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {metaRows.map((row) => (
                <div key={row.label} className="flex gap-3 border-b border-background-200 pb-3">
                  <dt className="w-16 shrink-0 text-sm text-foreground-500">
                    {row.label}
                  </dt>
                  <dd className="text-sm text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>

        <aside>
          <section className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">
              所属分类
            </h2>
            <div className="flex flex-wrap gap-2">
              <Link
                to={TYPE_PATHS[work.type]}
                className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-folder-line"></i>
                {TYPE_LABELS[work.type]}频道
              </Link>
              {work.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/search?q=${encodeURIComponent(genre)}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {genre}
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">
              快速导航
            </h2>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/" className="flex items-center gap-2 rounded-md px-3 py-2 text-foreground-700 transition-colors hover:bg-background-200">
                <i className="ri-home-4-line"></i>返回首页
              </Link>
              <Link to="/news" className="flex items-center gap-2 rounded-md px-3 py-2 text-foreground-700 transition-colors hover:bg-background-200">
                <i className="ri-newspaper-line"></i>影视资讯
              </Link>
              <Link to="/search" className="flex items-center gap-2 rounded-md px-3 py-2 text-foreground-700 transition-colors hover:bg-background-200">
                <i className="ri-search-line"></i>搜索中心
              </Link>
            </div>
          </section>
        </aside>
      </div>

      <section className="mt-10">
        <div className="mb-6 flex items-center gap-3">
          <span className="h-6 w-1.5 rounded-full bg-accent-500"></span>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            相关推荐
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {related.map((item) => (
            <WorkCard key={item.id} work={item} />
          ))}
        </div>
      </section>
    </div>
  );
}